<template>
  <div class="report-layout">
    <HeaderBar
      class="report-layout__header"
      :modifiers="['dark', 'sticky']"
    >
      <div>{{ pageTitle }}</div>

      <template #right>
        <BackLink
          class="button"
          :to="{ name: 'operation' }"
        >
          {{ $t('general.cancel') }}
        </BackLink>
        <button
          :class="{'button button--primary': true, 'button--disabled': dateValidationError || !imageValid}"
          :disabled="saving"
          type="button"
          @click="save"
        >
          <span :class="{ 'hidden': saving }">{{ !!siteHealthReportId ? $t('general.save') : $t('general.create_report') }}</span>
          <Loader
            v-if="saving"
            :modifiers="['overlay']"
          />
        </button>
      </template>
    </HeaderBar>

    <div class="report-layout__tabs-header">
      <SwitchButton
        v-model="tab"
        class="report-layout__tabs typo-small-title hide-for-large"
        :items="tabs"
        :modifiers="['soft', 'light']"
      />
    </div>

    <div class="report-layout__panels">
      <div :class="{ 'report-layout__panel report-layout__panel--scope': true, 'show-for-large': tab !== 'scope' }">
        <Loader
          v-if="loading"
          :modifiers="['overlay']"
        />
        <div class="form form--flat form--transparent">
          <hr class="form__group-line hide-for-large">
          <div class="form__group">
            <FormRow
              :errors="errors && errors.site_health_report.start_date"
              :label="$t('site_health_report.start_date')"
              labelFor="report_start_date"
              :modifiers="['lateral', 'narrow']"
            >
              <div class="form-row__input form-row__input--no-peek">
                <DatePicker
                  id="report_start_date"
                  v-model="report.start_date"
                  :placeholder="loading ? '' : $t('site_health_report.no_start_date')"
                />
              </div>
            </FormRow>
          </div>

          <div class="form__group">
            <FormRow
              :errors="errors && errors.site_health_report.end_date"
              :label="$t('site_health_report.end_date')"
              labelFor="report_end_date"
              :modifiers="['lateral', 'narrow', dateValidationError ? 'error' : null]"
            >
              <div class="form-row__input form-row__input--no-peek">
                <DatePicker
                  id="report_end_date"
                  v-model="report.end_date"
                  :minDate="report.start_date"
                  :placeholder="loading ? '' : $t('site_health_report.no_end_date')"
                />
              </div>
            </FormRow>
            <FormStandAloneErrorMessage
              v-if="dateValidationError"
              :errorMessage="$t('general.report_date_error')"
            />
          </div>

          <div class="form__group">
            <FormRow
              :errors="errors && errors.site_health_report.region_ids"
              :label="$t('site_health_report.regions')"
              labelFor="report_region_ids"
              :modifiers="['lateral', 'narrow']"
            >
              <div class="form-row__input form-row__input--no-peek">
                <RegionSelect
                  id="report_region_ids"
                  v-model="report.region_ids"
                  :placeholder="$t('site_health_report.none_specified')"
                />
              </div>
            </FormRow>
          </div>

          <div class="form__group">
            <FormRow
              :errors="errors && errors.site_health_report.inspector_ids"
              :label="$t('site_health_report.inspectors')"
              labelFor="report_inspector_ids"
              :modifiers="['lateral', 'narrow']"
            >
              <div class="form-row__input form-row__input--no-peek">
                <InspectorSelect
                  id="report_inspector_ids"
                  v-model="report.inspector_ids"
                  :placeholder="$t('site_health_report.none_specified')"
                />
              </div>
            </FormRow>
          </div>
        </div>
      </div>

      <div :class="{ 'report-layout__panel report-layout__panel--text': true, 'show-for-medium': tab !== 'text' }">
        <Loader v-if="loading" />
        <div
          v-if="!loading"
          class="scroll-container"
        >
          <div class="report-layout__text-form">
            <h2 class="report-layout__form-heading typo-ignore typo-small-title">
              <UseSvg
                class="icon icon--small"
                name="file_doc"
              />
              <span>{{ $t('site_health_report.title_page') }}</span>
            </h2>
            <div :class="{ 'textarea report-layout__textarea': true, 'textarea--error': errors && errors.site_health_report.title }">
              <label
                class="typo-small-title"
                for="report_title"
              >
                {{ $t('site_health_report.title') }}
              </label>
              <textarea
                id="report_title"
                v-model="report.title"
                v-autoresize
                class="typo-ignore typo-field-text"
                maxlength="128"
                rows="1"
              />
            </div>

            <div :class="{'report-layout__form-section': true, 'report-layout__form-section--error' : !imageValid}">
              <div class="report-layout__form-section-heading">
                {{ $t('site_health_report.logo') }}
              </div>
              <div class="report-layout__form-section-content">
                <div class="report-layout__logo-wrapper">
                  <img
                    v-if="report.logo"
                    class="report-layout__logo"
                    :src="report.logo"
                  >
                </div>
                <div class="report-layout__logo-actions">
                  <div class="report-layout__logo-input-wrapper">
                    <label
                      class="report-layout__logo-input-button button"
                      for="report_logo"
                    >
                      {{ $t('site_health_report.upload_image') }}
                    </label>
                    <input
                      id="report_logo"
                      :accept="acceptedFileTypes"
                      class="report-layout__logo-input"
                      type="file"
                      @change="selectLogo"
                    >
                  </div>
                  <button
                    v-if="report.logo"
                    :aria-label="$t('site_health_report.remove_image')"
                    class="report-layout__remove-logo-button button"
                    type="button"
                    @click="removeLogo"
                  >
                    <TrashSvg class="button__icon show-for-medium" />
                    <span class="hide-for-medium">{{ $t('site_health_report.remove_image') }}</span>
                  </button>
                </div>
              </div>
            </div>
            <FormStandAloneErrorMessage
              v-if="!imageValid"
              :errorMessage="imageValidationMessage"
            />
            <h2 class="report-layout__form-heading typo-ignore typo-small-title">
              <UseSvg
                class="icon icon--small"
                name="file_doc"
              />
              <span>{{ $t('site_health_report.exec_summary') }}</span>
            </h2>
            <div
              v-for="field in ['introduction', 'body', 'conclusion']"
              :key="field"
              class="textarea report-layout__textarea"
            >
              <label
                class="typo-small-title"
                :for="`report_exec_summary_${field}`"
              >
                {{ $t(`site_health_report.${field}`) }}
              </label>
              <textarea
                :id="`report_exec_summary_${field}`"
                v-model="report.data[`exec_summary_${field}`]"
                v-autoresize
                class="typo-ignore typo-field-text"
                rows="4"
              />
            </div>

            <h2 class="report-layout__form-heading typo-ignore typo-small-title">
              <UseSvg
                class="icon icon--small"
                name="file_doc"
              />
              <span>{{ $t('site_health_report.table_of_contents') }}</span>
            </h2>
            <h2 class="report-layout__form-heading typo-ignore typo-small-title">
              <UseSvg
                class="icon icon--small"
                name="file_doc"
              />
              <span>1. {{ $t('site_health_report.introduction') }}</span>
            </h2>
            <div
              v-for="field in ['introduction', 'body', 'conclusion']"
              :key="field"
              class="textarea report-layout__textarea"
            >
              <label
                class="typo-small-title"
                :for="`report_intro_${field}`"
              >
                {{ $t(`site_health_report.${field}`) }}
              </label>
              <textarea
                :id="`report_intro_${field}`"
                v-model="report.data[`intro_${field}`]"
                v-autoresize
                class="typo-ignore typo-field-text"
                rows="4"
              />
            </div>
            <h2 class="report-layout__form-heading typo-ignore typo-small-title">
              <UseSvg
                class="icon icon--small"
                name="file_doc"
              />
              <span>2. {{ $t('site_health_report.assessment_of_risk') }}</span>
            </h2>
            <h2 class="report-layout__form-heading typo-ignore typo-small-title">
              <UseSvg
                class="icon icon--small"
                name="file_doc"
              />
              <span>3. {{ $t('site_health_report.simm_scope') }}</span>
            </h2>
            <h2 class="report-layout__form-heading typo-ignore typo-small-title">
              <UseSvg
                class="icon icon--small"
                name="file_doc"
              />
              <span>4. {{ $t('site_health_report.inspection_findings') }}</span>
            </h2>
            <h2 class="report-layout__form-heading typo-ignore typo-small-title">
              <UseSvg
                class="icon icon--small"
                name="file_doc"
              />
              <span>{{ $t('site_health_report.conclusion') }}</span>
            </h2>
            <div class="textarea report-layout__textarea">
              <label
                class="typo-small-title"
                for="report_conclusion"
              >
                {{ $t('site_health_report.conclusion') }}
              </label>
              <textarea
                id="report_conclusion"
                v-model="report.data.conclusion"
                v-autoresize
                class="typo-ignore typo-field-text"
                rows="4"
              />
            </div>
          </div>
        </div>
      </div>

      <div :class="{ 'report-layout__panel report-layout__panel--issues': true, 'show-for-large': tab !== 'issues' }">
        <div
          v-if="!loadingIssues"
          class="report-layout__issue-count"
        >
          {{ $t('site_health_report.issues_count', { count: issues.length }) }}
        </div>
        <RecyclerView
          v-if="!loadingIssues && issues.length > 0"
          class="report-layout__issue-list"
          :count="issues.length"
          :gap="0.125"
          :height="4.25"
        >
          <template #default="{ index, style, key }">
            <IssueCard
              :key="key"
              :issue="issues[index]"
              noLink
              :responsive="false"
              :showDescription="false"
              :showLocation="false"
              :style="style"
            />
          </template>
        </RecyclerView>
        <EmptyMessage
          v-if="!loadingIssues && issues.length === 0"
          :message="$t('site_health_report.no_issues_message')"
          :title="$t('site_health_report.no_issues')"
        >
          <CircleSquareTargetSvg />
        </EmptyMessage>
        <Loader
          v-if="loadingIssues"
          class="report-layout__issues-loader"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, onMounted, reactive, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import I18n from 'i18n-js';

import { acceptedFileTypes } from '@/config/accepted_file_types';
import useUploader from '@/vendor/use_uploader';

import useAjax from '@/functions/use_ajax';
import useEmitter from '@/functions/use_emitter';
import { useBreakpoint } from '@/functions/screen_width';
import { isoDate } from '@/functions/date_format_helpers';
import readFile from '@/functions/read_file';

import vAutoresize from '@/directives/autoresize';

import HeaderBar from '@/components/generic/header_bar.vue';
import BackLink from '@/components/generic/back_link.vue';
import Loader from '@/components/generic/loader.vue';
import UseSvg from '@/components/generic/use_svg.vue';
import RecyclerView from '@/components/generic/recycler_view.vue';
import EmptyMessage from '@/components/generic/empty_message.vue';
import FormRow from '@/components/form/row.vue';
import DatePicker from '@/components/form/date_picker.vue';
import SwitchButton from '@/components/form/switch_button.vue';
import IssueCard  from '@/components/issues/issue_card.vue';
import RegionSelect from '@/components/reports/region_select.vue';
import InspectorSelect from '@/components/reports/inspector_select.vue';
import FormStandAloneErrorMessage from '@/components/form/form_stand_alone_error_message.vue';

import TrashSvg from '@/images/trash-32.svg';
import CircleSquareTargetSvg from '@/images/circle-square-target.svg';

const props = defineProps({
  organisationId: { type: String, required: true },
  operationId: { type: String, required: true },
  siteHealthReportId: { type: String, default: null },
  siteHealthReportTemplateId: { type: String, default: null },
});

const router = useRouter();
const uploader = useUploader();
const ajax = useAjax();
const emitter = useEmitter();

const tab = ref('scope');
const breakpoint = useBreakpoint();
const pageTitle = computed(() => {
  return I18n.t(`site_health_report.${props.siteHealthReportId ? 'edit' : 'new'}`);
});
const loading = ref(true);
const saving = ref(false);
const errors = ref(null);
const loadingIssues = ref(true);
const issues = ref(null);
const dateValidationError = ref(false);
const imageValid = ref(true);
const imageValidationMessage = ref(null);

let newLogoUpload = null;

const report = reactive(createNewReport());

const tabs = computed(() => {
  let items = [
    {
      label: I18n.t('site_health_report.scope'),
      value: 'scope',
    },
    {
      label: I18n.t('site_health_report.issues'),
      value: 'issues',
      tag: loadingIssues.value ? '...' : issues.value.length.toString(),
    }
  ];
  if (breakpoint.value === 'small') {
    items.push({
      label: I18n.t('site_health_report.text'),
      value: 'text',
    });
  }
  return items;
});

onMounted(async () => {
  await loadReport();
});

watch(breakpoint, () => {
  if (breakpoint.value !== 'small' && tab.value === 'text') {
    tab.value = 'scope';
  }
});

// trigger autoresize textareas to not be 0 when switching to "text" tab
watch(tab, async () => {
  await nextTick();
  window.dispatchEvent(new CustomEvent('resize'));
});

watch([
  () => report.start_date,
  () => report.end_date,
], startAndEndDate);

function createNewReport() {
  return {
    operation_id: props.operationId,
    title: '',
    logo: null,
    start_date: null,
    end_date: null,
    region_ids: [],
    inspector_ids: [],
    data: {
      exec_summary_introduction: '',
      exec_summary_body: '',
      exec_summary_conclusion: '',
      intro_introduction: '',
      intro_body: '',
      intro_conclusion: '',
      conclusion: '',
    },
  };
}

function parseDates(data) {
  return {
    ...data,
    start_date: data.start_date && new Date(data.start_date),
    end_date: data.end_date && new Date(data.end_date),
  };
}

async function fetchReport(reportId) {
  let { ok, data } = await ajax(`/organisations/${props.organisationId}/operations/${props.operationId}/plants/site_health_reports/${reportId}`);

  if (ok) {
    Object.assign(report, parseDates(data));
  }
}

async function loadReport() {
  loading.value = true;
  if (props.siteHealthReportId) {
    await fetchReport(props.siteHealthReportId);
  }
  else if (props.siteHealthReportTemplateId) {
    await fetchReport(props.siteHealthReportTemplateId);
    report.title = `${report.title} - ${I18n.t('operations.site_health_reports_copy')}`;
  } else {
    Object.assign(report, createNewReport());
  }

  loading.value = false;

  watch([
    () => report.start_date,
    () => report.end_date,
    () => report.inspector_ids,
    () => report.region_ids,
  ], loadIssues);

  loadIssues();
}

function startAndEndDate() {
  if ((report.end_date && report.start_date) && (new Date(report.end_date) < new Date(report.start_date))) {
    dateValidationError.value = true;
  } else {
    dateValidationError.value = false;
  }
}

async function loadIssues() {
  loadingIssues.value = true;
  issues.value = [];

  if (props.operationId && report.start_date && report.end_date) {
    let { ok, data } = await ajax.paginated(
      `/organisations/${props.organisationId}/operations/${props.operationId}/plants/issues`,
      { query: {
        start_date: isoDate(report.start_date),
        end_date: isoDate(report.end_date),
        inspector_ids: report.inspector_ids,
        region_ids: report.region_ids,
        category: 'approved_and_open'
      } },
    );

    if (ok) {
      issues.value = data;
    }
  }

  loadingIssues.value = false;
}

/**
 * @param {{ target: { files: File[] }}} param
 */
async function selectLogo({ target: { files: [file] } }) {
  if (!file) return;

  if (!acceptedFileTypes.includes(file.type)) {
    imageValidationMessage.value = I18n.t('general.image_validation_message', { filename: file.name });
    imageValid.value = false;
  } else {
    imageValid.value = true;
    let url = await readFile(file);
    report.logo = url;
    newLogoUpload = uploader.create(file);
  }
}

function removeLogo() {
  newLogoUpload = null;
  report.logo = null;
}

async function formatBodyForPost() {
  let data = {
    title: report.title,
    start_date: isoDate(report.start_date),
    end_date: isoDate(report.end_date),
    region_ids: report.region_ids,
    inspector_ids: report.inspector_ids,
    data: report.data,
  };

  if (newLogoUpload) {
    let blob = await uploader.upload(newLogoUpload);
    data.logo = blob.signed_id;
  } else if (!report.logo && props.siteHealthReportId) {
    data.remove_logo = true;
  }

  return { site_health_report: data };
}

async function save() {
  if (saving.value) return;

  saving.value = true;
  errors.value = null;
  let url = `/organisations/${props.organisationId}/operations/${props.operationId}/plants/site_health_reports`;
  let method = 'POST';
  if (props.siteHealthReportId) {
    url += `/${props.siteHealthReportId}`;
    method = 'PATCH';
  }
  let { ok, data } = await ajax(url, { method, body: await formatBodyForPost() });

  if (ok) {
    router.push({ name: 'operation' });
    emitter.emit('toast', { status: 'success', message: I18n.t('site_health_report.report_create_toast') });
  } else {
    errors.value = data;
  }

  saving.value = false;
}
</script>
